import Link from '@gik/core/components/Link.tsx';
import type { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import { useUserStore } from '@gik/core/store/UserStore';
import { useInkindCan } from '@gik/core/store/permissions';
import type { UIComponent } from '@gik/core/types/UI';
import type { HorVerLayout } from '@gik/core/types/layouts';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { isInkindPageOrganizer, isInkindPageOwner } from '@gik/inkind-page/utils/inkindPagePermissions';
import isInkindPageRecipientInfoComplete from '@gik/inkind-page/utils/isInkindPageRecipientInfoComplete';
import type { InkindAlgoliaObject } from '@gik/search/components';
import { HoverAnimation } from '@gik/ui-legacy/HoverAnimation';
import { DeterminationType } from '@gik/ui-legacy/LoadingLinear';
import { LoadingPopup } from '@gik/ui-legacy/gik/LoadingPopup/LoadingPopup';
import { NotInteractive } from '@gik/ui-legacy/NotInteractive/NotInteractive';
import { useInkindAPIOrAlgolia } from '@gik/user-profile/hooks/useInkindAPIOrAlgolia';
import type { Hit } from '@algolia/client-search';
import { InkindPageTileToolbar } from '../InkindPageTileToolbar/InkindPageTileToolbar';
import { InkindPageCardBase } from './InkindPageCardBase';

export type InkindPageCardProps = {
  inkindPage: InkindPageAPIModel | Hit<InkindAlgoliaObject>;
  onRemove?(
    inkindPageOrAlgolia: InkindPageAPIModel | Hit<InkindAlgoliaObject>,
    canEdit: boolean,
    isOrganizer: boolean
  ): void | Promise<void>;
  noToolbar?: boolean;
  pending?: boolean;
  layout?: HorVerLayout;
} & UIComponent;

const blockName = 'inkind-page-card';

function InkindPageCardComp({
  onRemove,
  inkindPage: inkindPageOrAlgolia,
  noToolbar,
  pending,
  layout,
}: InkindPageCardProps): JSX.Element {
  const bem = useBemCN(blockName);

  const { ownerId, slug, title, imgSrc, routeId, groupId, location, organizers } =
    useInkindAPIOrAlgolia(inkindPageOrAlgolia);
  const canEdit = useInkindCan('manage', routeId, groupId);
  const userId = useUserStore(store => store.id);
  const isOwner = isInkindPageOwner(ownerId, userId);
  const isOrganizer = isInkindPageOrganizer({ organizers }, userId);

  const onClickRemove = async (page: InkindPageAPIModel | Hit<InkindAlgoliaObject>) => {
    await onRemove(page, isOwner, isOrganizer);
  };

  const baseCard = (
    <InkindPageCardBase
      admin={canEdit}
      toolbar={
        !noToolbar ? (
          <InkindPageTileToolbar inkindPage={inkindPageOrAlgolia} onRemove={onClickRemove} />
        ) : null
      }
      imageUrl={imgSrc}
      title={title}
      location={isInkindPageRecipientInfoComplete(inkindPageOrAlgolia) && location}
      layout={layout}
    />
  );

  if (pending) {
    return (
      <div {...bem('pending-wrapper', null, 'tw-relative')}>
        <div style={{ filter: 'grayscale(1)', opacity: 0.6 }}>
          <NotInteractive>{baseCard}</NotInteractive>
        </div>
        <div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-100/40">
          <LoadingPopup text="Pending creation" type={DeterminationType.Indeterminate} progress={0} />
        </div>
      </div>
    );
  }

  return (
    <HoverAnimation {...bem('hover-wrapper')}>
      <div>
        <Link href={`/inkinds/${routeId}/${slug}`}>{baseCard}</Link>
      </div>
    </HoverAnimation>
  );
}

export const InkindPageCard = withComponentErrorBoundary(InkindPageCardComp);
